import type React from 'react';
import { Fragment } from 'react';

import { X } from 'lucide-react';
import { useField } from 'react-final-form';
import type { FieldArrayRenderProps } from 'react-final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';

import { Button, cn } from '#src/app/shared/components/adminShadcn';

import type { AudienceBuilderAllowedFilter, AudienceBuilderFilter } from '../../../shared/types';
import type { AudienceFilterVariant } from '../../variantStyles';
import { AUDIENCE_FILTER_VARIANT_STYLES } from '../../variantStyles';
import { LabeledSeparator } from '../LabeledSeparator';

import { FilterTypeField } from './components/FilterTypeField';
import { FilterValueField } from './components/FilterValueField';

const RemoveFilterButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
	return (
		<Button
			type="button"
			variant="outline"
			size="icon"
			aria-label="Remove filter"
			className="admin:shrink-0 admin:hover:border-destructive admin:hover:bg-destructive admin:hover:text-white"
			onClick={onClick}
		>
			<X />
		</Button>
	);
};

const AddOrButton: React.FC<{
	addItem: () => void;
	fieldTypeName: string;
	fieldValueName: string;
}> = ({ addItem, fieldTypeName, fieldValueName }) => {
	const fieldType = useField(fieldTypeName);
	const fieldValue = useField(fieldValueName);

	const isFilled = !!(fieldType.input.value && fieldValue.input.value);

	if (!isFilled) {
		return null;
	}

	return (
		<Button type="button" variant="link" size="sm" className="admin:self-start admin:px-0" onClick={addItem}>
			+ Add alternative (OR)
		</Button>
	);
};

export const OrFilterField: React.FC<{
	name: string;
	allowedFilters: readonly AudienceBuilderAllowedFilter[];
	// Adding this to type correctly the prop, the HTMLElement value is never read in the type definition
	fieldsAnd: FieldArrayRenderProps<AudienceBuilderFilter[], HTMLElement>['fields'];
	index: number;
	variant: AudienceFilterVariant;
}> = ({ name, allowedFilters, fieldsAnd, index, variant }) => {
	const styles = AUDIENCE_FILTER_VARIANT_STYLES[variant];
	return (
		<>
			<div
				className={cn(
					'admin:flex admin:flex-col admin:gap-2 admin:rounded-md admin:border admin:p-3 admin:shadow-sm',
					styles.card,
				)}
			>
				<FieldArray name={name} key={name}>
					{({ fields: fieldsOr }) =>
						fieldsOr.map((nameOr, indexOr) => (
							<Fragment key={`${nameOr}.type`}>
								{indexOr > 0 && (
									<LabeledSeparator label="OR" labelClassName={styles.orChip} lineClassName={styles.line} />
								)}
								<div className="admin:flex admin:w-full admin:items-start admin:gap-2">
									<FilterTypeField
										fieldName={`${nameOr}.type`}
										fieldValueName={`${nameOr}.value`}
										allowedFilters={allowedFilters}
									/>
									<FilterValueField
										fieldName={`${nameOr}.value`}
										fieldTypeName={`${nameOr}.type`}
										allowedFilters={allowedFilters}
									/>
									<RemoveFilterButton
										onClick={() => {
											if (fieldsOr.length && fieldsOr.length > 1) {
												fieldsOr.remove(indexOr);
											} else {
												fieldsAnd.remove(index);
											}
										}}
									/>
								</div>
								{!!fieldsOr.length && indexOr === fieldsOr.length - 1 && (
									<AddOrButton
										addItem={() =>
											fieldsOr.push({
												value: '',
												type: '',
											})
										}
										fieldTypeName={`${nameOr}.type`}
										fieldValueName={`${nameOr}.value`}
									/>
								)}
							</Fragment>
						))
					}
				</FieldArray>
			</div>
			{fieldsAnd.length && index !== fieldsAnd.length - 1 && (
				<LabeledSeparator
					label="AND"
					className="admin:my-2"
					labelClassName={styles.andChip}
					lineClassName={styles.line}
				/>
			)}
		</>
	);
};
